// Teacher-side lesson recording, done in the browser (free — LiveKit Egress
// minutes on the free plan cover only ~one lesson a month).
//
// The backend decides whether this lesson is recorded (POST /api/recordings/start):
// individual lessons only when the admin asked for it, group lessons always
// (the server later keeps the file only if a group member was absent).
//
// Composition, 1280×720: the slide (the ScreenShare track SlideCapturePublisher
// already publishes) on the left, everyone's camera tiles in a column on the
// right, all microphones mixed into one audio track. MediaRecorder emits a
// chunk every few seconds; chunks are uploaded strictly in order, so whatever
// was uploaded survives a closed tab. A reload starts a new part.
//
// Isolation: any failure here is logged and the lesson itself is unaffected.

import { useEffect, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { Track, RoomEvent } from 'livekit-client';
import { API } from '../../config/api';
import api, { fetchWithTimeout } from '../../utils/api';
import { useTranslation } from '../../i18n';

const WIDTH = 1280;
const HEIGHT = 720;
const SIDEBAR_WIDTH = 320;
const FPS = 15;
const TIMESLICE_MS = 4000;
// Upload pieces stay well under the backend's 2 MB chunk limit.
const PIECE_BYTES = 1024 * 1024;
const VIDEO_BITRATE = 900_000;
const AUDIO_BITRATE = 64_000;

const MIME_CANDIDATES = [
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9,opus',
  'video/webm',
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4',
];

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return null;
  return MIME_CANDIDATES.find(type => MediaRecorder.isTypeSupported(type)) || null;
}

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

// Timers in a hidden tab are throttled to 1/s; a worker's are not, so the
// recording keeps its frame rate while the teacher looks at another tab.
function createTicker(intervalMs, onTick) {
  try {
    const src = 'let id; onmessage = e => { clearInterval(id); if (e.data > 0) id = setInterval(() => postMessage(0), e.data); };';
    const url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
    const worker = new Worker(url);
    worker.onmessage = onTick;
    worker.postMessage(intervalMs);
    return () => { worker.terminate(); URL.revokeObjectURL(url); };
  } catch (_) {
    const id = setInterval(onTick, intervalMs);
    return () => clearInterval(id);
  }
}

function isRecordable(participant) {
  return !String(participant.identity || '').startsWith('__egress__');
}

function participantName(participant) {
  if (participant.name) return participant.name;
  return String(participant.identity || '').replace(/^user-/, '');
}

function liveTrack(participant, source) {
  const pub = participant.getTrackPublication(source);
  const mst = pub?.track?.mediaStreamTrack;
  if (!pub || pub.isMuted || !mst || mst.readyState !== 'live') return null;
  return mst;
}

// Draw `video` into the box, cropping (cover) or letterboxing (contain).
function drawVideo(ctx, video, x, y, w, h, mode) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return false;
  if (mode === 'cover') {
    const scale = Math.max(w / vw, h / vh);
    const sw = w / scale;
    const sh = h / scale;
    ctx.drawImage(video, (vw - sw) / 2, (vh - sh) / 2, sw, sh, x, y, w, h);
  } else {
    const scale = Math.min(w / vw, h / vh);
    const dw = vw * scale;
    const dh = vh * scale;
    ctx.drawImage(video, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  }
  return true;
}

// Builds the composed audio+video stream for one recording part.
function createComposer(room) {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');

  // Off-screen holder: some browsers don't decode <video> that isn't in the DOM.
  const holder = document.createElement('div');
  holder.setAttribute('aria-hidden', 'true');
  holder.style.cssText = 'position:fixed;left:-10000px;top:0;width:2px;height:2px;overflow:hidden;opacity:0;pointer-events:none;';
  document.body.appendChild(holder);

  const videos = new Map(); // MediaStreamTrack.id → <video>
  const videoFor = (mst) => {
    let el = videos.get(mst.id);
    if (!el) {
      el = document.createElement('video');
      el.muted = true;
      el.playsInline = true;
      el.autoplay = true;
      el.srcObject = new MediaStream([mst]);
      holder.appendChild(el);
      el.play().catch(() => {});
      videos.set(mst.id, el);
    }
    return el;
  };

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const audioCtx = new AudioCtx();
  audioCtx.resume().catch(() => {});
  const audioDest = audioCtx.createMediaStreamDestination();
  const audioSources = new Map(); // MediaStreamTrack.id → MediaStreamAudioSourceNode

  const participants = () =>
    [room.localParticipant, ...room.remoteParticipants.values()].filter(p => p && isRecordable(p));

  const syncAudio = () => {
    const live = new Set();
    for (const p of participants()) {
      const pub = p.getTrackPublication(Track.Source.Microphone);
      const mst = pub?.track?.mediaStreamTrack;
      if (!mst || mst.readyState !== 'live') continue;
      live.add(mst.id);
      if (!audioSources.has(mst.id)) {
        const node = audioCtx.createMediaStreamSource(new MediaStream([mst]));
        node.connect(audioDest);
        audioSources.set(mst.id, node);
      }
    }
    for (const [id, node] of audioSources) {
      if (!live.has(id)) {
        try { node.disconnect(); } catch (_) {}
        audioSources.delete(id);
      }
    }
  };

  const draw = () => {
    const usedVideos = new Set();
    ctx.fillStyle = '#1f1f1f';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    // Slide area
    const slide = liveTrack(room.localParticipant, Track.Source.ScreenShare);
    if (slide) {
      usedVideos.add(slide.id);
      drawVideo(ctx, videoFor(slide), 0, 0, WIDTH - SIDEBAR_WIDTH, HEIGHT, 'contain');
    }

    // Camera tiles: one column up to 4 people, two columns beyond that.
    const people = participants();
    const cols = people.length <= 4 ? 1 : 2;
    const rows = Math.max(1, Math.ceil(people.length / cols));
    const gap = 4;
    const tileW = (SIDEBAR_WIDTH - gap * (cols + 1)) / cols;
    const tileH = Math.min((tileW * 9) / 16, (HEIGHT - gap * (rows + 1)) / rows);
    const top = (HEIGHT - (rows * tileH + (rows - 1) * gap)) / 2;
    const left = WIDTH - SIDEBAR_WIDTH + gap;

    people.forEach((p, i) => {
      const x = left + (i % cols) * (tileW + gap);
      const y = top + Math.floor(i / cols) * (tileH + gap);
      ctx.fillStyle = '#3a3a3a';
      ctx.fillRect(x, y, tileW, tileH);

      const name = participantName(p);
      const cam = liveTrack(p, Track.Source.Camera);
      let drawn = false;
      if (cam) {
        usedVideos.add(cam.id);
        drawn = drawVideo(ctx, videoFor(cam), x, y, tileW, tileH, 'cover');
      }
      if (!drawn) {
        ctx.fillStyle = '#d4d4d4';
        ctx.font = `600 ${Math.round(tileH * 0.35)}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText((name[0] || '?').toUpperCase(), x + tileW / 2, y + tileH / 2);
      }

      const fontSize = cols === 1 ? 14 : 11;
      ctx.font = `500 ${fontSize}px sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      const label = name.length > 22 ? `${name.slice(0, 21)}…` : name;
      const labelW = Math.min(tileW - 8, ctx.measureText(label).width + 10);
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(x + 4, y + tileH - fontSize - 10, labelW, fontSize + 6);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(label, x + 9, y + tileH - 6, tileW - 18);
    });

    // Forget <video> elements of tracks that went away.
    for (const [id, el] of videos) {
      if (!usedVideos.has(id)) {
        el.srcObject = null;
        el.remove();
        videos.delete(id);
      }
    }
  };

  let tick = 0;
  draw();
  syncAudio();
  const stopTicker = createTicker(Math.round(1000 / FPS), () => {
    draw();
    tick += 1;
    if (tick % FPS === 0) syncAudio();
  });

  const videoTrack = canvas.captureStream(FPS).getVideoTracks()[0];
  const stream = new MediaStream([videoTrack, ...audioDest.stream.getAudioTracks()]);

  const dispose = () => {
    stopTicker();
    stream.getTracks().forEach(t => t.stop());
    for (const node of audioSources.values()) {
      try { node.disconnect(); } catch (_) {}
    }
    audioSources.clear();
    audioCtx.close().catch(() => {});
    for (const el of videos.values()) el.srcObject = null;
    videos.clear();
    holder.remove();
  };

  return { stream, dispose };
}

async function putChunk(recordingId, seq, blob, isRetry = false) {
  try {
    const res = await fetchWithTimeout(
      `${API.RECORDINGS}/${recordingId}/chunks/${seq}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: blob,
      },
      60 * 1000,
    );
    if (res.ok) return { ok: true };
    if (res.status === 401 && !isRetry) {
      const refreshed = await api.refreshToken();
      return refreshed.ok ? putChunk(recordingId, seq, blob, true) : { ok: false };
    }
    // The server closed this part (stale, wrong order, file gone) — start a new one.
    if ([403, 404, 409, 410].includes(res.status)) return { ok: false, fatal: true };
    return { ok: false };
  } catch (_) {
    return { ok: false };
  }
}

// Sequential, in-order uploader with retry/backoff for flaky connections.
function createUploader(recordingId, onFatal) {
  const queue = [];
  let seq = 0;
  let running = false;
  let closed = false;
  let idleWaiters = [];

  const settleIdle = () => {
    idleWaiters.forEach(resolve => resolve());
    idleWaiters = [];
  };

  const pump = async () => {
    if (running) return;
    running = true;
    let backoff = 1000;
    while (queue.length > 0 && !closed) {
      const result = await putChunk(recordingId, seq, queue[0]);
      if (result.ok) {
        queue.shift();
        seq += 1;
        backoff = 1000;
      } else if (result.fatal) {
        closed = true;
        queue.length = 0;
        onFatal();
      } else {
        await sleep(backoff);
        backoff = Math.min(backoff * 2, 30 * 1000);
      }
    }
    running = false;
    settleIdle();
  };

  return {
    push(blob) {
      if (closed) return;
      for (let offset = 0; offset < blob.size; offset += PIECE_BYTES) {
        queue.push(blob.slice(offset, offset + PIECE_BYTES));
      }
      pump();
    },
    whenIdle() {
      if (!running && queue.length === 0) return Promise.resolve();
      return new Promise(resolve => idleWaiters.push(resolve));
    },
    isClosed: () => closed,
  };
}

// One recording part: composer → MediaRecorder → uploader.
function startPart(room, recordingId, mimeType, onFatal) {
  const composer = createComposer(room);
  const uploader = createUploader(recordingId, onFatal);
  let recorder;
  try {
    recorder = new MediaRecorder(composer.stream, {
      mimeType,
      videoBitsPerSecond: VIDEO_BITRATE,
      audioBitsPerSecond: AUDIO_BITRATE,
    });
  } catch (err) {
    composer.dispose();
    throw err;
  }
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) uploader.push(e.data);
  };
  recorder.start(TIMESLICE_MS);

  let stopping = null;
  const stop = () => {
    if (stopping) return stopping;
    stopping = (async () => {
      if (recorder.state !== 'inactive') {
        await new Promise((resolve) => {
          recorder.addEventListener('stop', resolve, { once: true });
          try { recorder.stop(); } catch (_) { resolve(); }
        });
      }
      composer.dispose();
      await uploader.whenIdle();
      if (!uploader.isClosed()) {
        await api.post(`${API.RECORDINGS}/${recordingId}/finish`, {});
      }
    })().catch(err => console.warn('[lesson-recorder] stop failed:', err));
    return stopping;
  };

  return { stop };
}

export default function LessonRecorder({ lessonId }) {
  const { t } = useTranslation();
  const room = useRoomContext();
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    if (!room || !lessonId) return undefined;
    const mimeType = pickMimeType();
    if (!mimeType || typeof HTMLCanvasElement.prototype.captureStream !== 'function') {
      console.warn('[lesson-recorder] this browser cannot record');
      return undefined;
    }

    let cancelled = false;
    let part = null;
    let retryTimer = null;
    let stopTimer = null;
    let onConnected = null;

    const endPart = () => {
      clearTimeout(stopTimer);
      const current = part;
      part = null;
      if (!cancelled) setRecording(false);
      return current ? current.stop() : Promise.resolve();
    };

    const begin = async () => {
      if (cancelled || part) return;
      const { data, error } = await api.post(`${API.RECORDINGS}/start`, { lessonId, mimeType });
      if (cancelled || error || !data) return;
      if (!data.record) {
        // Teacher came in early — try again once the recording window opens.
        if (data.retryAt) {
          const wait = Math.max(1000, new Date(data.retryAt).getTime() - Date.now() + 1000);
          retryTimer = setTimeout(begin, Math.min(wait, 2147483647));
        }
        return;
      }
      try {
        part = startPart(room, data.recordingId, mimeType, () => {
          // Server rejected the part — close it and open a fresh one.
          endPart().then(() => {
            if (!cancelled) retryTimer = setTimeout(begin, 2000);
          });
        });
      } catch (err) {
        console.warn('[lesson-recorder] failed to start:', err);
        return;
      }
      setRecording(true);
      const untilStop = new Date(data.stopAt).getTime() - Date.now();
      stopTimer = setTimeout(endPart, Math.max(0, Math.min(untilStop, 2147483647)));
    };

    if (room.state === 'connected') {
      begin();
    } else {
      onConnected = () => begin();
      room.once(RoomEvent.Connected, onConnected);
    }

    return () => {
      cancelled = true;
      if (onConnected) room.off(RoomEvent.Connected, onConnected);
      clearTimeout(retryTimer);
      endPart();
    };
  }, [room, lessonId]);

  if (!recording) return null;
  return (
    <div
      className="fixed bottom-4 left-4 z-[60] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-medium select-none"
      title={t('video.recordingTitle')}
    >
      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
      {t('video.recording')}
    </div>
  );
}
